import * as tokens from '@utmost.co/utmost-design-tokens';
import type { CSSProperties } from 'react';
import { Components } from '../../../styles';
import { DEFAULT_BUTTON_INPUT_ELEVATION } from './commons';
import { CSSObject } from '@mui/styled-engine';

const BUTTON_SIZE_SMALL = {
  minHeight: 32,
  fontSize: 12,
  paddingLeft: 16,
  paddingRight: 16
};

const BUTTON_SIZE_MEDIUM = {
  minHeight: 40,
  fontSize: 14,
  paddingLeft: 20,
  paddingRight: 20
};

const BUTTON_SIZE_LARGE = {
  minHeight: 48,
  fontSize: 14,
  paddingLeft: 24,
  paddingRight: 24
};

const disabledOutlined: CSSProperties = {
  borderColor: tokens.stateOnSurfaceDisabledContainerStateLayerColor, //1px solid var(--state-on-surface-disabled-container-state-layer-color, rgba(26, 26, 26, 0.12));
  backgroundColor: tokens.stateOnSurfaceDisabledContainerStateLayerColor, //var(--state-on-surface-disabled-container-state-layer-color, rgba(26, 26, 26, 0.12));
  color: tokens.stateOnSurfaceDisabledContentStateLayerColor
};
const disabledText: CSSProperties = {
  color: tokens.stateOnSurfaceDisabledContentStateLayerColor
};

export const VARIANT = {
  contained: {
    '&:hover': DEFAULT_BUTTON_INPUT_ELEVATION,
    '&:focus': {
      boxShadow: `none`
    }
  },
  outlined: {
    borderColor: tokens.colorOutline,
    '&:hover': {
      ...DEFAULT_BUTTON_INPUT_ELEVATION,
      borderColor: tokens.colorOutline
    },
    '&:focus': {
      boxShadow: `none`,
      borderColor: tokens.colorOutline
    },
    '&.Mui-disabled': disabledOutlined
  }
} satisfies Record<string, CSSObject>;

export const COLOR_DEFAULT = {
  default: {
    '&.Mui-disabled': disabledText
  },
  contained: {
    background: tokens.colorSurfaceVariant,
    color: tokens.colorOnSurface,
    '&:hover': {
      background: `rgba(26, 26, 26, 0.08)`
    },
    '&:focus': {
      background: `rgba(26, 26, 26, 0.16)`
    }
  }
} satisfies Record<string, CSSObject>;

export const COLOR_PRIMARY = {
  default: {
    color: tokens.colorPrimary,
    '&:hover': {
      background: tokens.statePrimaryHoverStateLayerColor
    },
    '&:focus': {
      background: tokens.statePrimaryFocusStateLayerColor
    },
    '&.Mui-disabled': disabledText
  },
  contained: {
    color: tokens.colorOnPrimary,
    backgroundColor: tokens.colorPrimary,
    '&:hover': {
      background: tokens.paletteBlue50
    },
    '&:focus': {
      background: tokens.paletteBlue60
    }
  },
  outlined: {
    borderColor: tokens.colorPrimary,
    '&:hover': {
      borderColor: tokens.colorPrimary
    },
    '&:focus': {
      borderColor: tokens.colorPrimary
    }
  }
} satisfies Record<string, CSSObject>;

export const COLOR_SECONDARY = {
  default: {
    color: tokens.colorAlert,
    '&:hover': {
      background: tokens.stateAlertHoverStateLayerColor
    },
    '&:focus': {
      background: tokens.stateAlertFocusStateLayerColor
    },
    '&.Mui-disabled': disabledText
  },
  contained: {
    color: tokens.colorOnAlert,
    backgroundColor: tokens.colorAlert,
    '&:hover': {
      background: tokens.paletteRed50 // @migrate tokens.v5 '#c22a1f'
    },
    '&:focus': {
      background: tokens.paletteRed60 // @migrate tokens v5 '#c53429'
    }
  },
  outlined: {
    borderColor: tokens.colorAlert,
    '&:hover': {
      borderColor: tokens.colorAlert
    },
    '&:focus': {
      borderColor: tokens.colorAlert
    }
  }
} satisfies Record<string, CSSObject>;

export const buttonTheme = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 400,
        boxShadow: 'none',
        '&[aria-current="true"]': {
          pointerEvents: 'none'
        },
        variants: [
          {
            props: { variant: 'contained' },
            style: VARIANT.contained
          },
          {
            props: { variant: 'outlined' },
            style: VARIANT.outlined
          },
          {
            props: { size: 'small' },
            style: BUTTON_SIZE_SMALL
          },
          {
            props: { size: 'medium' },
            style: BUTTON_SIZE_MEDIUM
          },
          {
            props: { size: 'large' },
            style: BUTTON_SIZE_LARGE
          },
          {
            props: { color: 'default' },
            style: COLOR_DEFAULT.default
          },
          {
            props: { color: 'default', variant: 'contained' },
            style: COLOR_DEFAULT.contained
          },
          {
            props: { color: 'primary' },
            style: COLOR_PRIMARY.default
          },
          {
            props: { color: 'primary', variant: 'contained' },
            style: COLOR_PRIMARY.contained
          },
          {
            props: { color: 'primary', variant: 'outlined' },
            style: COLOR_PRIMARY.outlined
          },
          {
            props: { color: 'secondary' },
            style: COLOR_SECONDARY.default
          },

          {
            props: { color: 'secondary', variant: 'contained' },
            style: COLOR_SECONDARY.contained
          },
          {
            props: { color: 'secondary', variant: 'outlined' },
            style: COLOR_SECONDARY.outlined
          }
        ]
      }
    }
  }
} satisfies Components;
